import _ from 'lodash'
import type { MouseEvent } from 'react'
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination'
import { cn } from '@/lib/utils'

function getVisiblePages(currentPage: number, totalPages: number) {
	if (totalPages <= 5) {
		return _.times(totalPages, (index) => index + 1)
	}

	if (currentPage <= 3) {
		return [1, 2, 3, 'ellipsis', totalPages] as const
	}

	if (currentPage >= totalPages - 2) {
		return [1, 'ellipsis', totalPages - 2, totalPages - 1, totalPages] as const
	}

	return [1, 'ellipsis', currentPage, 'ellipsis', totalPages] as const
}

interface ProfilePaginationProps {
	page: number
	perPage: number
	total: number
	onPageChange: (page: number) => void
}

export function ProfilePagination({ page, perPage, total, onPageChange }: ProfilePaginationProps) {
	const totalPages = Math.max(1, Math.ceil(total / perPage))
	const start = total === 0 ? 0 : (page - 1) * perPage + 1
	const end = Math.min(page * perPage, total)
	const visiblePages = getVisiblePages(page, totalPages)

	const handlePageChange = (nextPage: number) => (event: MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault()

		if (nextPage < 1 || nextPage > totalPages || nextPage === page) {
			return
		}

		onPageChange(nextPage)
	}

	return (
		<div className='flex flex-col gap-3 px-4 py-4 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between'>
			<p>
				Showing {start}-{end} of {total}
			</p>
			<Pagination className='mx-0 w-auto justify-start sm:justify-end'>
				<PaginationContent>
					<PaginationItem>
						<PaginationPrevious href='#' onClick={handlePageChange(page - 1)} className={cn('h-9 px-3', page <= 1 && 'pointer-events-none opacity-50')} />
					</PaginationItem>
					{_.map(visiblePages, (item, index) => (
						<PaginationItem key={`${item}-${index}`}>
							{item === 'ellipsis' ? (
								<PaginationEllipsis />
							) : (
								<PaginationLink href='#' isActive={item === page} onClick={handlePageChange(item)} className='h-9 w-9'>
									{item}
								</PaginationLink>
							)}
						</PaginationItem>
					))}
					<PaginationItem>
						<PaginationNext href='#' onClick={handlePageChange(page + 1)} className={cn('h-9 px-3', page >= totalPages && 'pointer-events-none opacity-50')} />
					</PaginationItem>
				</PaginationContent>
			</Pagination>
		</div>
	)
}
